import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Divider, Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Tooltip, CircularProgress, Chip, TablePagination, Alert, MenuItem, TextField,
  Dialog, DialogContent, DialogActions,
} from '@mui/material';
import { Add, Delete, ReceiptLong, Refresh, Visibility, Download, Close, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';
import InvoicePreview from '../components/InvoicePreview';
import { colors } from '../theme/theme';
import { generateInvoicePdf } from '../utils/generateInvoicePdf';

const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<any>(null);

  const [viewInvoice, setViewInvoice] = useState<any>(null);
  const [pdfInvoice, setPdfInvoice] = useState<any>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const pdfRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, sum]: any = await Promise.all([
        apiService.getInvoices({ page: page + 1, limit: 20 }),
        apiService.getInvoiceRevenueSummary(),
      ]);
      setInvoices(res.invoices || []);
      setTotal(res.total || 0);
      setSummary(sum || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [page]);

  // Row writes are pinned to the invoice's own location — under "All
  // locations" the switcher sends __all__, which the server refuses for writes.
  const handleStatus = async (id: string, status: string, studioId?: string | null) => {
    try {
      await apiService.updateInvoiceStatus(id, status, studioId);
      load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update status');
    }
  };

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string, studioId?: string | null) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await apiService.deleteInvoice(id, studioId);
      load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete');
    }
  };

  const handleDownload = async (inv: any) => {
    setDownloadingId(inv._id);
    setPdfInvoice(inv);
    setError(null);
    try {
      await generateInvoicePdf('invoice-pdf-preview', `${inv.invoiceNumber || 'invoice'}.pdf`);
    } catch {
      setError('Could not generate the PDF.');
    } finally {
      setPdfInvoice(null);
      setDownloadingId(null);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <ReceiptLong sx={{ color: colors.primary }} />
          <Typography variant="h5" fontWeight={700}>Invoices</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/invoice-generator')} sx={{ backgroundColor: colors.primary }}>
            New Invoice
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {summary && (
        <Card sx={{ mb: 2, border: `1px solid ${colors.divider}`, backgroundColor: '#2e7d3209' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Invoiced income (paid · ₹ INR)</Typography>
              <Typography variant="h6" fontWeight={800} sx={{ color: '#2e7d32' }}>
                ₹{(summary.invoicedIncome || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary">Paid invoices</Typography>
              <Typography fontWeight={700}>{summary.count || 0}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Tax collected</Typography>
              <Typography fontWeight={700}>₹{(summary.taxCollected || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', maxWidth: 260 }}>
              Reporting only — excluded from payouts/settlement. Non-₹ invoices aren't counted.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Card sx={{ border: `1px solid ${colors.divider}` }}>
        {loading ? (
          <Box p={4} textAlign="center"><CircularProgress /></Box>
        ) : invoices.length === 0 ? (
          <Box p={5} textAlign="center">
            <ReceiptLong sx={{ fontSize: 40, color: `${colors.primary}55`, mb: 1 }} />
            <Typography color="text.secondary">No invoices yet. Create your first one.</Typography>
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Invoice #</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Bill To</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Branding</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map(inv => (
                  <TableRow key={inv._id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{inv.invoiceNumber}</TableCell>
                    <TableCell>{inv.billTo?.name || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={inv.studio?.studioName || 'SweatSocial'}
                        sx={{ backgroundColor: `${colors.primary}15`, color: colors.primary }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {inv.currencySymbol || '₹'}{(inv.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>
                      <TextField
                        select size="small" variant="standard"
                        value={inv.status || 'draft'}
                        onChange={(e) => handleStatus(inv._id, e.target.value, studioIdOf(inv.studio))}
                        sx={{ minWidth: 84 }}
                        InputProps={{ disableUnderline: true }}
                      >
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="sent">Sent</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell>{fmtDate(inv.invoiceDate)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => setViewInvoice(inv)}><Visibility fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => navigate(`/invoice-generator/${inv._id}`)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Download PDF">
                        <span>
                          <IconButton size="small" onClick={() => handleDownload(inv)} disabled={downloadingId === inv._id}>
                            {downloadingId === inv._id ? <CircularProgress size={16} /> : <Download fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(inv._id, studioIdOf(inv.studio))}><Delete fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div" count={total} page={page} rowsPerPage={20}
              rowsPerPageOptions={[20]} onPageChange={(_, p) => setPage(p)}
            />
          </>
        )}
      </Card>

      <Dialog open={!!viewInvoice} onClose={() => setViewInvoice(null)} maxWidth="md" fullWidth>
        <DialogContent sx={{ backgroundColor: '#f5f5f5', display: 'flex', justifyContent: 'center', p: 3, overflow: 'auto' }}>
          {viewInvoice && (
            <Box sx={{ boxShadow: '0 4px 24px rgba(0,0,0,0.12)', backgroundColor: '#fff' }}>
              <InvoicePreview invoice={viewInvoice} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Close />} onClick={() => setViewInvoice(null)}>Close</Button>
          <Button
            variant="contained" startIcon={<Download />}
            onClick={() => viewInvoice && handleDownload(viewInvoice)}
            disabled={!!downloadingId}
            sx={{ backgroundColor: colors.primary }}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>

      {pdfInvoice && (
        <Box sx={{ position: 'fixed', left: -99999, top: 0, pointerEvents: 'none' }} aria-hidden>
          <InvoicePreview ref={pdfRef} captureId="invoice-pdf-preview" invoice={pdfInvoice} />
        </Box>
      )}
    </Box>
  );
};

export default InvoicesPage;
